import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FieldConfig, FieldSectionConfig } from "../../../../../models/Models";
import { ClassificationMethodLabels, FpbSummary } from "../../../../../models/vod/Models";
import { useEditFpbSummary } from "../../../../../services/vod/Query";
import EditForm from "./EditForm";
import Modal from "../../../../../context/modal/Modal";
import { toDateTimeString, toIsoDate } from "../../../../../utils/DateUtil";

interface Props {
    modalId: string;
    content: FpbSummary;
    openModal: boolean;
    onClose: () => void;
    onlyCertificateFields?: boolean;
}

const FpbSummaryModal = ({ content, modalId, openModal, onClose, onlyCertificateFields = false }: Props) => {
    const editFpbSummary = useEditFpbSummary();
    const [ managedFpbSummary, setManagedFpbSummary ] = useState({
        fpbSummary: content,
        isUpdated: false,
    });

    const classificationMethodOptions: Record<string, string> = ClassificationMethodLabels;

    useEffect(() => {
        setManagedFpbSummary({
            isUpdated: false,
            fpbSummary: content,
        });
    }, [ content ]);

    useEffect(() => {
        const runTime = managedFpbSummary.fpbSummary.runTime;
        const licensingWindow = managedFpbSummary.fpbSummary.licensingWindow;
        if (!!runTime && !managedFpbSummary.isUpdated) {
            const updatedRunTime = dayjs
                .duration(runTime).format("HH:mm:ss");
            setManagedFpbSummary({
                isUpdated: true,
                fpbSummary: {
                    ...managedFpbSummary.fpbSummary,
                    licensingWindow: {
                        startDateTime: toDateTimeString(licensingWindow?.startDateTime),
                        endDateTime: toDateTimeString(licensingWindow?.endDateTime),
                    },
                    runTime: updatedRunTime,
                },
            });
        }
    }, [ managedFpbSummary, content ]);

    const fpbFormConfig: FieldConfig<FpbSummary>[] = [
        { path: "certificateReferenceNumber", label: "Certificate Reference Number", type: "text" },
        { path: "classificationMethod", label: "Classification Method", type: "select", options: classificationMethodOptions },
        { path: "classificationIssueDate", label: "Classification Issue Date", type: "date" },
        { path: "certificateLocation", label: "Certificate Upload", type: "file", allowedFiles: [ "application/pdf" ] },
    ];
    const contentFormConfig: FieldConfig<FpbSummary>[] = [
        { path: "assetId", label: "Asset Id", type: "text" },
        { path: "contentPartnerName", label: "Content Partner Name", type: "text" },
        { path: "contentPartnerId", label: "Content Partner Id", type: "text" },
        { path: "title", label: "Title", type: "text" },
        ...content.itemType !== "SERIES"
            ? [ { path: "seasonNumberAndEpisodeNumber", label: "Season Number And Episode Number", type: "text" } as FieldConfig<FpbSummary> ]
        : [],
        { path: "categories", label: "Categories", type: "text", isArray: true },
        { path: "runTime", label: "Runtime", type: "duration" },
        { path: "ageRating", label: "Age Rating", type: "text" },
        { path: "advisories", label: "Advisories", type: "text", isArray: true },
        {
            path: "licensingWindow",
            type: "group",
            fields: [
                { path: "startDateTime", label: "Licensing Window Start", type: "datetime-local" },
                { path: "endDateTime", label: "Licensing Window End", type: "datetime-local" },
            ],
        },
    ];

    const fieldSectionConfigs: FieldSectionConfig<FpbSummary>[] = onlyCertificateFields
        ? [ { fields: fpbFormConfig } ]
        : [
            {
                heading: "FPB",
                fields: fpbFormConfig,
            },
            {
                heading: "Content",
                fields: contentFormConfig,
            },
        ];

    const submit = async (data: FpbSummary) => {
        let runTime = data.runTime;

        if (runTime) {
            const splitDuration = runTime.split(":");

            if (splitDuration.length === 3) {
                const [ hours, minutes, seconds ] = splitDuration.map(timeUnit => Number(timeUnit));
                runTime = dayjs.duration({ hours, minutes, seconds }).toISOString();
            }
        }

        await editFpbSummary.mutateAsync({
            ...data,
            runTime: runTime,
            licensingWindow: {
                startDateTime: toIsoDate(data.licensingWindow?.startDateTime),
                endDateTime: toIsoDate(data.licensingWindow?.endDateTime),
            },
        });
    };

    return (
        <Modal
            modalTitle={"Edit"}
            modalId={modalId}
            modalSize={onlyCertificateFields ? "w-[60vw] h-[50vh]" : "w-[60vw] h-[80vh]"}
            openModal={openModal} onClose={onClose}
        >
            <EditForm
                modalId={modalId}
                config = {fieldSectionConfigs}
                initialValues = {managedFpbSummary.fpbSummary}
                onSubmit = {submit}
            />
        </Modal>
    );
};

export default FpbSummaryModal;